import React, { useState } from 'react';
import { signIn, signUp, confirmSignUp, resetPassword, confirmResetPassword, signOut } from 'aws-amplify/auth';
import '../styles/AuthComponents.css';

type AuthMode = 'signIn' | 'signUp' | 'forgotPassword' | 'resetPassword';

interface AuthProps {
  onAuthSuccess: (user: any) => void;
  onCancel: () => void;
}

const AuthComponents: React.FC<AuthProps> = ({ onAuthSuccess, onCancel }) => {
  const [mode, setMode] = useState<AuthMode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userCreated, setUserCreated] = useState(false);

  const validateForm = () => {
    setError(null);
    setSuccessMessage(null);

    if (!email.trim()) {
      setError('Email is required');
      return false;
    }

    if (mode === 'signIn' || mode === 'signUp') {
      if (!password) {
        setError('Password is required');
        return false;
      }
    }

    if (mode === 'signUp') {
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }
      
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    if (mode === 'resetPassword' && !verificationCode) {
      setError('Verification code is required');
      return false;
    }

    return true;
  };

  const handleSignIn = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Clear any existing session to avoid conflicts
      try {
        await signOut();
      } catch {
        // Ignore signOut errors - user may not be signed in
      }

      const { isSignedIn } = await signIn({
        username: email,
        password,
      });

      const user = { username: email, isSignedIn };
      setIsLoading(false);
      onAuthSuccess(user);
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to sign in');
    }
  };

  const handleSignUp = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      setIsLoading(false);
      setUserCreated(true);
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to sign up');
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError('Verification code is required');
      return;
    }

    setIsLoading(true);
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode,
      });
      setIsLoading(false);
      setUserCreated(false);
      setVerificationCode('');
      setMode('signIn');
      setSuccessMessage('Account verified! You can now sign in.');
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to verify code');
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword({
        username: email,
      });
      setIsLoading(false);
      setMode('resetPassword');
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to request password reset');
    }
  };

  const handleResetPassword = async () => {
    if (!validateForm() || !verificationCode) {
      setError('All fields are required');
      return;
    }

    setIsLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: password,
      });
      setIsLoading(false);
      setVerificationCode('');
      setMode('signIn');
      setSuccessMessage('Password reset successful. You can now sign in.');
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to reset password');
    }
  };

  const renderSignIn = () => (
    <div className="auth-form">
      <h2>Sign In</h2>
      <div className="form-group">
        <label>Email</label>
        <input 
          type="email" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <div className="form-group">
        <label>Password</label>
        <input 
          type="password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <div className="button-group">
        <button 
          className="primary-button" 
          onClick={handleSignIn}
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </button>
        <button 
          className="secondary-button" 
          onClick={() => setMode('signUp')}
          disabled={isLoading}
        >
          Create Account
        </button>
      </div>
      <p className="auth-link" onClick={() => setMode('forgotPassword')}>
        Forgot Password?
      </p>
    </div>
  );

  const renderSignUp = () => (
    <div className="auth-form">
      <h2>Create Account</h2>
      {userCreated ? (
        <>
          <p className="success-message">
            Account created! Check your email for a verification code.
          </p>
          <div className="form-group">
            <label>Verification Code</label>
            <input 
              type="text" 
              value={verificationCode} 
              onChange={(e) => setVerificationCode(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="button-group">
            <button 
              className="primary-button" 
              onClick={handleVerifyCode}
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="form-group">
            <label>Email</label>
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label>Confirm Password</label>
            <input 
              type="password" 
              value={confirmPassword} 
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="button-group">
            <button 
              className="primary-button" 
              onClick={handleSignUp}
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
            <button 
              className="secondary-button" 
              onClick={() => setMode('signIn')}
              disabled={isLoading}
            >
              Back to Sign In
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderForgotPassword = () => (
    <div className="auth-form">
      <h2>Reset Password</h2>
      <div className="form-group">
        <label>Email</label>
        <input 
          type="email" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <div className="button-group">
        <button 
          className="primary-button" 
          onClick={handleForgotPassword}
          disabled={isLoading}
        >
          {isLoading ? 'Sending Code...' : 'Send Reset Code'}
        </button>
        <button 
          className="secondary-button" 
          onClick={() => setMode('signIn')}
          disabled={isLoading}
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );

  const renderResetPassword = () => (
    <div className="auth-form">
      <h2>Set New Password</h2>
      <p className="success-message">
        Reset code sent! Check your email.
      </p>
      <div className="form-group">
        <label>Email</label>
        <input 
          type="email" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <div className="form-group">
        <label>Reset Code</label>
        <input 
          type="text" 
          value={verificationCode} 
          onChange={(e) => setVerificationCode(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <div className="form-group">
        <label>New Password</label>
        <input 
          type="password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <div className="form-group">
        <label>Confirm New Password</label>
        <input 
          type="password" 
          value={confirmPassword} 
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <div className="button-group">
        <button 
          className="primary-button" 
          onClick={handleResetPassword}
          disabled={isLoading}
        >
          {isLoading ? 'Resetting Password...' : 'Reset Password'}
        </button>
        <button 
          className="secondary-button" 
          onClick={() => setMode('signIn')}
          disabled={isLoading}
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );

  return (
    <div className="auth-container">
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      {mode === 'signIn' && renderSignIn()}
      {mode === 'signUp' && renderSignUp()}
      {mode === 'forgotPassword' && renderForgotPassword()}
      {mode === 'resetPassword' && renderResetPassword()}
    </div>
  );
};

export default AuthComponents;
